import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Heading,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Link,
  ListItem,
  OrderedList,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Search } from 'lucide-react';
import { useT } from '../i18n';
import {
  HELP_ENTRIES,
  HELP_TOPICS,
  searchHelp,
  type HelpEntry,
} from '../lib/helpContent';
import { useSiteConfig } from '../lib/siteConfig';
import { storeLinksOf } from '../lib/storeLinks';
import { StoreBadges } from '../components/site/StoreBadges';
import {
  ReadingColumn,
  SiteShell,
  richText,
} from '../components/site/SiteChrome';

// Help (#92, docs/help.md D290): how-to guides and questions, public so
// someone who can't sign in can read it too. Each entry has its own link
// (/help#lost-phone) that opens it.

function EntryBody({ entry, text }: { entry: HelpEntry; text: string }) {
  if (entry.kind === 'guide') {
    return (
      <OrderedList spacing={2} pl={1} fontSize="17px" lineHeight="26px">
        {text
          .split('\n')
          .filter(Boolean)
          .map((step, index) => (
            <ListItem key={index} color="ink.body">
              {step}
            </ListItem>
          ))}
      </OrderedList>
    );
  }
  return (
    <VStack align="stretch" spacing={3}>
      {text.split('\n\n').map((paragraph, index) => (
        <Text key={index} fontSize="17px" lineHeight="26px" color="ink.body">
          {paragraph}
        </Text>
      ))}
    </VStack>
  );
}

export function HelpPage() {
  const t = useT();
  const { hash } = useLocation();
  const siteConfig = useSiteConfig();
  const supportEmail = siteConfig?.supportEmail ?? null;
  // "Install Twinden" leads with the store apps once a link is set (D346).
  const storeLinks = storeLinksOf(siteConfig);
  const hasStoreLinks = !!(
    storeLinks.appStore ||
    storeLinks.playStore ||
    storeLinks.apk
  );
  const [query, setQuery] = useState('');
  // Opened entries by id: the one a link points at starts open.
  const [open, setOpen] = useState<string[]>(() =>
    hash ? [decodeURIComponent(hash.slice(1))] : []
  );

  useEffect(() => {
    const id = hash ? decodeURIComponent(hash.slice(1)) : '';
    if (!id) return;
    setOpen((current) => (current.includes(id) ? current : [...current, id]));
    // After the entry has rendered open.
    const timer = window.setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ block: 'start' });
    }, 50);
    return () => window.clearTimeout(timer);
  }, [hash]);

  const words = (entry: HelpEntry) => `${t(entry.title)}\n${t(entry.body)}`;
  const shown = useMemo(
    () => searchHelp(HELP_ENTRIES, query, words),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [query, t]
  );

  const section = (topicEntries: HelpEntry[]) => {
    const ids = topicEntries.map((entry) => entry.id);
    return (
      <Accordion
        allowMultiple
        index={ids
          .map((id, index) => (open.includes(id) ? index : -1))
          .filter((index) => index >= 0)}
        onChange={(expanded) => {
          const now = (Array.isArray(expanded) ? expanded : [expanded]).map(
            (index) => ids[index]!
          );
          setOpen((current) => [
            ...current.filter((id) => !ids.includes(id)),
            ...now,
          ]);
        }}
      >
        {topicEntries.map((entry) => (
          <AccordionItem key={entry.id} borderColor="line">
            {/* The link's anchor (/help#lost-phone), clear of the header. */}
            <Box as="h4" id={entry.id} scrollMarginTop="80px">
              <AccordionButton px={1} py={4} minH="56px">
                <Text
                  as="span"
                  flex="1"
                  textAlign="start"
                  fontWeight={700}
                  fontSize="18px"
                >
                  {t(entry.title)}
                </Text>
                <AccordionIcon />
              </AccordionButton>
            </Box>
            <AccordionPanel px={1} pb={5}>
              {entry.id === 'install' && hasStoreLinks && (
                <VStack align="stretch" spacing={3} pb={4}>
                  <Text fontSize="17px" lineHeight="26px" color="ink.body">
                    {t('help.g.install.apps')}
                  </Text>
                  <StoreBadges links={storeLinks} all />
                </VStack>
              )}
              <EntryBody entry={entry} text={t(entry.body)} />
            </AccordionPanel>
          </AccordionItem>
        ))}
      </Accordion>
    );
  };

  return (
    <SiteShell>
      <ReadingColumn>
        <VStack align="stretch" spacing={8} pb={16}>
          <VStack align="stretch" spacing={4}>
            <Heading
              as="h1"
              fontSize={{ base: '36px', md: '48px' }}
              lineHeight={1.05}
            >
              {t('help.title')}
            </Heading>
            <Text fontSize="20px" lineHeight="30px" color="ink.body">
              {t('help.intro')}
            </Text>
            <Text fontSize="15px" color="muted">
              {t('help.webNote')}
            </Text>
            <InputGroup size="lg">
              <InputLeftElement pointerEvents="none" color="muted">
                <Search size={20} />
              </InputLeftElement>
              <Input
                type="search"
                placeholder={t('help.search')}
                aria-label={t('help.search')}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                borderRadius="full"
              />
            </InputGroup>
          </VStack>

          {/* Jump to either part. */}
          <HStack spacing={3} flexWrap="wrap">
            <Button
              as="a"
              href="#faq"
              variant="outline"
              colorScheme="gray"
              borderColor="line"
              h="44px"
              borderRadius="full"
            >
              {t('help.jumpFaq')}
            </Button>
            <Button
              as="a"
              href="#guides"
              variant="outline"
              colorScheme="gray"
              borderColor="line"
              h="44px"
              borderRadius="full"
            >
              {t('help.guides')}
            </Button>
          </HStack>

          {shown.length === 0 ? (
            <Text fontSize="17px" color="muted" data-testid="help-empty">
              {t('help.noResults', { query })}
            </Text>
          ) : (
            // The questions first, then the guides (owner, 2026-10-28), each by topic.
            (['question', 'guide'] as const).map((kind) => {
              const ofKind = shown.filter((entry) => entry.kind === kind);
              if (ofKind.length === 0) return null;
              return (
                <Box
                  as="section"
                  key={kind}
                  id={kind === 'question' ? 'faq' : 'guides'}
                  scrollMarginTop="80px"
                >
                  <Heading
                    as="h2"
                    fontSize={{ base: '28px', md: '32px' }}
                    pb={4}
                  >
                    {t(kind === 'question' ? 'help.questions' : 'help.guides')}
                  </Heading>
                  <VStack align="stretch" spacing={6}>
                    {HELP_TOPICS.map((topic) => {
                      const topicEntries = ofKind.filter(
                        (entry) => entry.topic === topic
                      );
                      if (topicEntries.length === 0) return null;
                      return (
                        <Box key={topic}>
                          <Heading
                            as="h3"
                            fontSize="15px"
                            fontWeight={700}
                            color="muted"
                            pb={1}
                          >
                            {t(`help.topic.${topic}`)}
                          </Heading>
                          {section(topicEntries)}
                        </Box>
                      );
                    })}
                  </VStack>
                </Box>
              );
            })
          )}

          <Box as="section" bg="accent.tint" borderRadius="20px" px={5} py={5}>
            <Heading as="h2" fontSize="22px" pb={2}>
              {t('help.stillStuck')}
            </Heading>
            <Text fontSize="17px" lineHeight="26px" color="ink.body">
              {t('help.askFamily')}
            </Text>
            {supportEmail && (
              <Text fontSize="17px" lineHeight="26px" color="ink.body" pt={2}>
                {richText(t('help.writeToUs'), {
                  email: (
                    <Link
                      href={`mailto:${supportEmail}`}
                      color="accent"
                      fontWeight={700}
                      wordBreak="break-all"
                    >
                      {supportEmail}
                    </Link>
                  ),
                })}
              </Text>
            )}
          </Box>
        </VStack>
      </ReadingColumn>
    </SiteShell>
  );
}
